import React from 'react';
import PropTypes from 'prop-types';

/**
 * Kanonische Card des Design-Systems (siehe MODERNIZATION_PLAN.md P1.3).
 *
 * Standardisiert Radius und Schatten (shadow-token-*) — löst den Radius-/
 * Schatten-Wildwuchs (rounded-lg/xl/2xl + rohe shadow-lg/xl gemischt) für
 * neuen/migrierten Code.
 *
 * Radius 16px (rounded-2xl) seit Okt 2026, wie die Inhaltskarten im Werbespot
 * und auf der Website (Motion-Kit `.eo-session`, `.eo-journal`, `.eo-jpage`).
 * Knöpfe, Felder und Kacheln bleiben bei 12px — eine Karte ist das Gefäß, sie
 * darf eine Stufe weicher sein als das, was in ihr liegt.
 *
 * Varianten:
 * - flat: nur Rand, kein Schatten (z.B. verschachtelte Karten)
 * - raised: Standard — dezenter Schatten, abgehobene Fläche
 * - interactive: wie raised + Hover/Focus-Zustand; mit onClick klickbar
 *   und per Tastatur bedienbar (Enter/Space)
 * - glass: durchscheinend, nur fuer dunkle Buehnen (Fokus-Session)
 */

const VARIANTS = {
  flat: 'bg-surface border border-border',
  raised: 'bg-surface border border-border shadow-token-sm',
  interactive:
    'bg-surface border border-border shadow-token-sm transition-[box-shadow,border-color] duration-base ' +
    'hover:shadow-token-md hover:border-primary/30 cursor-pointer ' +
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
  // glass: durchscheinende Karte fuer dunkle Buehnen (Fokus-Session ueber der
  // Aura-Szene). Ohne eigene Textfarbe — die kommen von den Dark-Tokens des
  // umgebenden `.dark`-Wrappers. Nicht auf hellem Grund einsetzen.
  glass: 'bg-white/[0.06] border border-white/10 backdrop-blur-md',
};

const Card = React.forwardRef(
  ({ variant = 'raised', onClick, className = '', children, ...rest }, ref) => {
    const isInteractive = variant === 'interactive' || Boolean(onClick);
    const interactionProps = onClick
      ? {
          role: 'button',
          tabIndex: 0,
          onClick,
          onKeyDown: (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onClick(e);
            }
          },
        }
      : {};

    return (
      <div
        ref={ref}
        className={[
          'rounded-2xl',
          VARIANTS[isInteractive ? 'interactive' : variant] || VARIANTS.raised,
          className,
        ].join(' ')}
        {...interactionProps}
        {...rest}
      >
        {children}
      </div>
    );
  }
);

Card.displayName = 'Card';
Card.propTypes = {
  variant: PropTypes.oneOf(['flat', 'raised', 'interactive', 'glass']),
  onClick: PropTypes.func,
  className: PropTypes.string,
  children: PropTypes.node,
};

export default Card;
